import { getCalendlyUtm } from "./tracking-url";

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (options: {
        url: string;
        parentElement: HTMLElement;
        prefill?: Record<string, unknown>;
        utm?: Record<string, unknown>;
      }) => void;
    };
  }
}

const SCRIPT_ID = "calendly-widget-script";
const CSS_ID = "calendly-widget-css";
const HOST_ID = "calendly-host";
const SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
const CSS_HREF = "https://assets.calendly.com/assets/external/widget.css";

export const WIDGET_HEIGHT = 700;

/** Light theme, to match the landing page. */
export const CALENDLY_URL =
  "https://calendly.com/d/ctz2-69b-999/discovery-call-neosearch?hide_gdpr_banner=1&background_color=ffffff&text_color=0f172a&primary_color=f97316";

export function injectCalendlyCss() {
  if (typeof document === "undefined" || document.getElementById(CSS_ID)) return;
  const link = document.createElement("link");
  link.id = CSS_ID;
  link.rel = "stylesheet";
  link.href = CSS_HREF;
  document.head.appendChild(link);
}

export function injectCalendlyScript(): HTMLScriptElement | null {
  if (typeof document === "undefined") return null;
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) return existing as HTMLScriptElement;
  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.async = true;
  document.body.appendChild(script);
  return script;
}

/** Executes the widget ahead of /demo so window.Calendly exists on arrival. */
export function warmCalendly() {
  if (typeof window === "undefined") return;
  const run = () => {
    injectCalendlyCss();
    injectCalendlyScript();
  };
  const ric = (
    window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
    }
  ).requestIdleCallback;
  if (ric) ric(run, { timeout: 2500 });
  else window.setTimeout(run, 1200);
}

// The scheduler lives in one body-level node that is never reparented — moving
// an iframe in the DOM discards its browsing context and reloads it, which is
// exactly the cost we're avoiding. /demo positions this node over its slot
// rather than rendering a widget of its own.
let host: HTMLDivElement | null = null;
let mounted = false;
let stopTracking: (() => void) | null = null;

function park(el: HTMLDivElement) {
  el.style.cssText =
    `position:fixed;left:-10000px;top:0;width:420px;height:${WIDGET_HEIGHT}px;` +
    "z-index:10;background:#fff";
}

function ensureHost(): HTMLDivElement {
  if (host) return host;
  host = document.createElement("div");
  host.id = HOST_ID;
  park(host);
  document.body.appendChild(host);
  return host;
}

/** Builds the scheduler before /demo is opened. Safe to call repeatedly. */
export function mountCalendly() {
  if (mounted || typeof window === "undefined") return;
  mounted = true;
  injectCalendlyCss();
  const el = ensureHost();
  const init = () => {
    window.Calendly?.initInlineWidget({
      url: CALENDLY_URL,
      parentElement: el,
      utm: getCalendlyUtm(),
    });
  };
  if (window.Calendly) init();
  else injectCalendlyScript()?.addEventListener("load", init, { once: true });
}

/**
 * Positions the host over /demo's slot, in document coordinates so scrolling
 * moves it for free. Tracked per frame rather than by ResizeObserver: the slot
 * also *moves* without resizing (grid reflow, a rewrapped headline on language
 * switch), and an observer never fires for that. Writes only on change.
 */
export function attachCalendly(slot: HTMLElement) {
  mountCalendly();
  const el = ensureHost();
  let frame = 0;
  let px = NaN;
  let py = NaN;
  let pw = NaN;
  let ph = NaN;

  const sync = () => {
    const r = slot.getBoundingClientRect();
    const x = r.left + window.scrollX;
    const y = r.top + window.scrollY;
    if (x !== px || y !== py || r.width !== pw || r.height !== ph) {
      px = x;
      py = y;
      pw = r.width;
      ph = r.height;
      el.style.position = "absolute";
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.width = `${r.width}px`;
      el.style.height = `${r.height}px`;
    }
    frame = requestAnimationFrame(sync);
  };

  sync();
  stopTracking = () => cancelAnimationFrame(frame);
}

export function detachCalendly() {
  stopTracking?.();
  stopTracking = null;
  if (host) park(host);
}
